import {Component, Inject, OnInit} from '@angular/core';
import {UserService} from '../user/user.service';
import {AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidatorFn, Validators} from '@angular/forms';
import {ResetState} from './reset-state';
import {TranslatePipe} from '@ngx-translate/core';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatButton} from '@angular/material/button';
import {MatInput} from '@angular/material/input';
import {NavStatusService} from '../nav-status.service';
import {NavigationPoint} from '../navigation-point';
import {Router, RouterLink} from '@angular/router';
import {PasswordStrength} from './password-strength';
import {Subscription} from 'rxjs';
import {DOCUMENT, NgClass} from '@angular/common';
import {MatIcon} from '@angular/material/icon';

@Component({
  selector: 'app-reset-password',
    imports: [
        TranslatePipe,
        MatFormFieldModule,
        ReactiveFormsModule,
        MatButton,
        MatInput,
        NgClass,
        MatIcon,
        RouterLink
    ],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss'
})
export class ResetPasswordComponent implements OnInit {
  emailForm: FormGroup;
  codeForm: FormGroup;
  pwForm: FormGroup;
  public errorMessage: string | undefined;
  state: ResetState = ResetState.init;
  passwordSubscription: Subscription | undefined;
  prdDomain: string | undefined = undefined;
  domainsToCheck: string[] = ['head', 'mares', 'tyrolia', 'divessi', 'zoggs' ];
  isTestServer: boolean = false;
  currentStrength = PasswordStrength.STRENGTH_VERY_WEAK;

  constructor(private userService: UserService, private fb: FormBuilder,
              private navService: NavStatusService,
              private router: Router,
              @Inject(DOCUMENT) private document: Document) {
    this.emailForm = fb.group({
      email: ['', [Validators.required, Validators.email]],
    })
    this.codeForm = fb.group({
      email: ['', []],
      code: ['', [Validators.required]],
    })
    this.pwForm = fb.group({
      email: ['', []],
      code: ['', []],
      password: ['', [Validators.required]],
      password2: ['', [Validators.required]],
    },{
      validators: this.matchValidator('password', 'password2')
    })
  }
  ngOnInit(): void {
    this.navService.setActiveNavPoint(NavigationPoint.login);
    this.isTestServer = this.checkForTst();
  }
  checkForTst(): boolean {
    const domain = document.location.host;
    console.log('domain?', domain)
    let i: number = domain.indexOf('test');
    if (i !== -1 || domain === 'localhost:4200') {
      for(const entry of this.domainsToCheck) {
        let k: number = domain.indexOf(entry);
        if (k !== -1) {
          this.prdDomain = 'https://dam.' + entry + '.com';
          return true
        }
      }
      this.prdDomain = 'http://localhost:4200/login';
      return true;

    }
    return false;
  }
  matchValidator(controlName: string, matchingControlName: string): ValidatorFn {
    return (abstractControl: AbstractControl) => {
      const control = abstractControl.get(controlName);
      const matchingControl = abstractControl.get(matchingControlName);

      if (matchingControl!.errors && !matchingControl!.errors?.['confirmedValidator']) {
        return null;
      }

      if (control!.value !== matchingControl!.value) {
        const error = {confirmedValidator: 'Passwords do not match.'};
        matchingControl!.setErrors(error);
        return error;
      } else {
        matchingControl!.setErrors(null);
        return null;
      }
    };
  }
  estimateStrength(password: string): PasswordStrength {
    if (!password.length) {
      return PasswordStrength.STRENGTH_VERY_WEAK;
    }

    const charCounts: Record<number, number> = {};
    for (const char of password) {
      const code = char.charCodeAt(0);
      charCounts[code] = (charCounts[code] || 0) + 1;
    }

    let control = 0, digit = 0, upper = 0, lower = 0, symbol = 0, other = 0;
    const chars = Object.keys(charCounts).length;

    for (const chr in charCounts) {
      const code = parseInt(chr, 10);
      switch (true) {
        case code < 32 || code === 127:
          control = 33;
          break;
        case code >= 48 && code <= 57:
          digit = 10;
          break;
        case code >= 65 && code <= 90:
          upper = 26;
          break;
        case code >= 97 && code <= 122:
          lower = 26;
          break;
        case code >= 128:
          other = 128;
          break;
        default:
          symbol = 33;
      }
    }

    const pool = lower + upper + digit + symbol + control + other;
    const entropy = chars * Math.log2(pool) + (password.length - chars) * Math.log2(chars);

    switch (true) {
      case entropy >= 120:
        return PasswordStrength.STRENGTH_VERY_STRONG;
      case entropy >= 100:
        return PasswordStrength.STRENGTH_STRONG;
      case entropy >= 80:
        return PasswordStrength.STRENGTH_MEDIUM;
      case entropy >= 60:
        return PasswordStrength.STRENGTH_WEAK;
      default:
        return PasswordStrength.STRENGTH_VERY_WEAK;
    }
  }


  goBackEmail() {
    this.state = ResetState.init;
  }
  sendEmail(): void {
    this.userService.resetPassword(this.emailForm.controls['email'].value).subscribe({
      next: (res: any) => {
        if (res.ok) {
          this.codeForm.patchValue({
            email: this.emailForm.controls['email'].value,
          });
          this.codeForm.controls['email'].disable();
          this.state = ResetState.code;
          this.errorMessage = undefined;
        }
      },
      error:(err: any)  => {
        this.errorMessage = err;
      }
    });
  }
  sendCode(): void {
    this.userService.resetPassword(this.emailForm.controls['email'].value,
        this.codeForm.controls['code'].value).subscribe({
      next: (res: any) => {
        if (res.ok) {
          this.pwForm.patchValue({
            code: this.codeForm.controls['code'].value,
            email: this.emailForm.controls['email'].value,
          })
          this.pwForm.controls['email'].disable();
          this.pwForm.controls['code'].disable();
          this.errorMessage = undefined;
          this.state = ResetState.setPassword;
          this.passwordSubscription = this.pwForm.controls['password'].valueChanges.subscribe(
              value => {
                if (value) {
                  this.currentStrength = this.estimateStrength(value);
                }
              }
          )
        }
      },
      error:(err: any) => {
        this.errorMessage = err;
      }
    })
  }
  sendPassword(): void {
    this.userService.resetPassword(this.emailForm.controls['email'].value,
        this.codeForm.controls['code'].value,
        this.pwForm.controls['password'].value).subscribe({
      next: (res: any) => {
        if (res.ok) {
          this.router.navigate(['/login']).then();
        }
      },
      error:(err: any) => {
        this.errorMessage = err;
      }
    })
  }


  protected readonly ResetState = ResetState;
  protected readonly PasswordStrength = PasswordStrength;
}
